/**
 * Panel-wide "pane" seam, in the style of `messageFooters.ts`: a full-width
 * view that replaces the chat area inside the open panel, toggled from a
 * header icon button next to the panel's existing actions (history, new
 * conversation, expand, close). A package appends one entry here, e.g. P5's
 * admin-only "System knowledge" pane (`systemKnowledgePane.tsx`); an empty
 * array means no extra header button renders. `AgentPanel` renders one
 * entry per registered pane, each behind its own small header-button
 * component so every hook below is called unconditionally by that one
 * component instance — never from inside a `.map()` callback.
 */
import type { ComponentType, FC } from "react";
import type { SvgIconProps } from "@mui/material";
import {
  AgentChangelog,
  AgentChangelogIcon,
  useAgentChangelogTitle,
  useAgentChangelogUnseenBadge,
  useAgentChangelogVisible,
} from "./AgentChangelog.js";
import { systemKnowledgePane } from "./systemKnowledgePane.js";

export type AgentPanelPane = {
  /** Stable key; also used to remember which pane is currently open. */
  id: string;
  /** The header button's tooltip/aria-label; a hook so it can call `useTranslate`. */
  useTitle: () => string;
  icon: ComponentType<SvgIconProps>;
  /** Whether the header button (and thus the pane) is offered at all. */
  useVisible: () => boolean;
  /** Drives a dot badge on the header button, e.g. for open proposals. */
  useBadge?: () => boolean;
  /** Rendered in place of the chat while this pane is open; no props. */
  Component: FC;
};

export const agentPanelPanes: AgentPanelPane[] = [
  {
    id: "changelog",
    useTitle: useAgentChangelogTitle,
    icon: AgentChangelogIcon,
    useVisible: useAgentChangelogVisible,
    useBadge: useAgentChangelogUnseenBadge,
    Component: AgentChangelog,
  },
  systemKnowledgePane,
];
